/**
 * Pure booking rules: the query/filter mapping onto the verified ZOY-250
 * contract, conflict extraction from a refused save, and the lifecycle
 * guards that mirror (never replace) the backend's own rules.
 */

import { ApiError } from '@/core/api/errors';
import type { QueryFilter } from '@/core/api/querySpec';
import {
  BOOKING_QUERY_MAX_RANGE_DAYS,
  BookingErrorCode,
  isLocationType,
  type BookingStatus,
  type BookingView,
  type JoinKind,
  type LocationType,
} from '@/core/clinic/bookingsApi';
import type { GroupSessionView } from '@/core/clinic/groupSessionsApi';

/** ZOY-693: the three-way mode label — never collapsed to a binary. */
export const modeLabelKey = (locationType: string): string =>
  `bk.mode.${isLocationType(locationType) ? locationType : 'in_clinic'}`;

/** ZOY-685: a service whose occurrences are group sessions with attendees. */
export const isGroupService = (s: { workflowType: string | null } | null | undefined): boolean =>
  s?.workflowType === 'group_session';

/** A participation: its time, therapist, room and destination belong to the occurrence. */
export const isParticipation = (b: Pick<BookingView, 'groupSessionId'>): boolean =>
  typeof b.groupSessionId === 'string' && b.groupSessionId.length > 0;

/**
 * Calendar and My Day render a group occurrence ONCE (never one block per
 * attendee): participations are dropped and each occurrence becomes one
 * item carrying its count. The item keeps the booking shape so the grid,
 * agenda and list render it with the same code; `group` marks it.
 */
export interface GroupMarker {
  id: string;
  participantCount: number;
  capacity: number;
  executionStatus: GroupSessionView['executionStatus'];
}
export type CalendarItem = BookingView & { group?: GroupMarker };

export function occurrenceItem(g: GroupSessionView): CalendarItem {
  const execution: BookingView['executionStatus'] =
    g.executionStatus === 'cancelled'
      ? 'cancelled'
      : g.executionStatus === 'in_progress'
        ? 'in_progress'
        : g.executionStatus === 'completed'
          ? 'completed'
          : 'scheduled';
  return {
    id: `group:${g.id}`,
    patientId: '',
    serviceId: g.serviceId,
    practitionerUid: g.practitionerUid,
    locationType: g.locationType,
    roomId: g.roomId,
    packageItemId: null,
    startsAt: g.startsAt,
    endsAt: g.endsAt,
    visitAddress: null,
    travelAllowanceMinutes: 0,
    status: g.status === 'cancelled' ? 'cancelled' : 'booked',
    statusReason: g.statusReason,
    startedAt: g.startedAt,
    startedBy: g.startedBy,
    joinKind: g.joinKind,
    joinUrl: g.joinUrl,
    groupSessionId: g.id,
    createdBy: g.createdBy,
    createdAt: g.createdAt,
    service: g.service,
    patient: { firstName: '', lastName: '', registered: true },
    delivered: false,
    executionStatus: execution,
    group: {
      id: g.id,
      participantCount: g.participantCount,
      capacity: g.capacity,
      executionStatus: g.executionStatus,
    },
  };
}

/** The list carries every participation; the occurrence list carries the group. Merge to one item each. */
export function collapseGroupItems(
  bookings: readonly BookingView[],
  occurrences: readonly GroupSessionView[],
): CalendarItem[] {
  return [...bookings.filter((b) => !isParticipation(b)), ...occurrences.map(occurrenceItem)].sort(
    (x, y) => Date.parse(x.startsAt) - Date.parse(y.startsAt) || x.id.localeCompare(y.id),
  );
}

/** The group list has no server filters; the calendar's own apply here, client-side. */
export function occurrenceMatches(
  g: GroupSessionView,
  f: Pick<
    BookingWindowFilters,
    'practitionerUid' | 'roomId' | 'serviceId' | 'locationType' | 'status'
  >,
): boolean {
  if (f.practitionerUid && g.practitionerUid !== f.practitionerUid) return false;
  if (f.roomId && g.roomId !== f.roomId) return false;
  if (f.serviceId && g.serviceId !== f.serviceId) return false;
  if (f.locationType && g.locationType !== f.locationType) return false;
  if (f.status && f.status !== 'all') {
    const wanted = f.status.includes('booked')
      ? 'scheduled'
      : f.status.includes('cancelled')
        ? 'cancelled'
        : null;
    if (wanted === null || g.status !== wanted) return false;
  }
  return true;
}

/** ZOY-684: an online session is saved only with a destination the backend accepts. */
export function destinationReady(
  mode: LocationType,
  joinKind: JoinKind | '',
  joinUrl: string,
  isValidUrl: (u: string) => boolean,
): boolean {
  if (mode !== 'online') return true;
  if (joinKind === 'zoyaar_hosted') return true;
  return joinKind === 'external_url' && isValidUrl(joinUrl.trim());
}

export interface BookingWindowFilters {
  from: Date;
  to: Date;
  practitionerUid?: string;
  roomId?: string;
  serviceId?: string;
  patientId?: string;
  packageItemId?: string;
  status?: readonly BookingStatus[] | 'all';
  locationType?: string;
}

/** Builds the allow-listed filters; the startsAt window is mandatory by contract. */
export function bookingFilters(f: BookingWindowFilters): QueryFilter[] {
  const out: QueryFilter[] = [
    { field: 'startsAt', op: 'between', value: [f.from.toISOString(), f.to.toISOString()] },
  ];
  if (f.practitionerUid) out.push({ field: 'practitionerUid', op: 'eq', value: f.practitionerUid });
  if (f.roomId) out.push({ field: 'roomId', op: 'eq', value: f.roomId });
  if (f.serviceId) out.push({ field: 'serviceId', op: 'eq', value: f.serviceId });
  if (f.patientId) out.push({ field: 'patientId', op: 'eq', value: f.patientId });
  if (f.packageItemId) out.push({ field: 'packageItemId', op: 'eq', value: f.packageItemId });
  if (f.status && f.status !== 'all') {
    out.push(
      f.status.length === 1
        ? { field: 'status', op: 'eq', value: f.status[0] }
        : { field: 'status', op: 'in', value: [...f.status] },
    );
  }
  if (f.locationType) out.push({ field: 'locationType', op: 'eq', value: f.locationType });
  return out;
}

export function windowTooWide(from: Date, to: Date): boolean {
  return (to.getTime() - from.getTime()) / 86_400_000 > BOOKING_QUERY_MAX_RANGE_DAYS;
}

/**
 * Every violation a refused save returned, in order: the engine's
 * `violations[]` when present, else the single typed code (exclusion
 * arbiter / not-active / delivered). Null when the error is not a refusal.
 */
export function conflictCodes(err: unknown): string[] | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 409 && err.status !== 400) return null;
  const body = err.body as { violations?: unknown } | undefined;
  if (Array.isArray(body?.violations) && body.violations.length > 0) {
    return body.violations.filter((v): v is string => typeof v === 'string');
  }
  return err.code ? [err.code] : null;
}

/**
 * ZOY-650: a practitioner / room overlap caused ONLY by the clinic gap (the
 * sessions themselves do not touch) carries `gapConflicts` and `gapMinutes`
 * on the 409 body, so the refusal can name the gap instead of an overlap the
 * calendar cannot show. A real overlap, or the database race guard, carries
 * neither — then this is null.
 */
export interface GapConflict {
  subjects: readonly ('practitioner' | 'room')[];
  minutes: number;
}

export function gapConflictOf(err: unknown): GapConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { gapConflicts?: unknown; gapMinutes?: unknown } | undefined;
  if (!Array.isArray(body?.gapConflicts) || typeof body.gapMinutes !== 'number') return null;
  const subjects = body.gapConflicts.filter(
    (s): s is 'practitioner' | 'room' => s === 'practitioner' || s === 'room',
  );
  return subjects.length > 0 ? { subjects, minutes: body.gapMinutes } : null;
}

/** The subject a violation code is about, for the gap wording. */
export const gapSubjectOf = (code: string): 'practitioner' | 'room' | null =>
  code === BookingErrorCode.PractitionerOverlap
    ? 'practitioner'
    : code === BookingErrorCode.RoomOverlap
      ? 'room'
      : null;

/** Which of the designed "ways out" a conflict list suggests. */
export function conflictWaysOut(codes: readonly string[]): {
  time: boolean;
  practitioner: boolean;
  room: boolean;
} {
  const has = (c: string) => codes.includes(c);
  return {
    time:
      has(BookingErrorCode.PatientOverlap) ||
      has(BookingErrorCode.PractitionerOverlap) ||
      has(BookingErrorCode.RoomOverlap) ||
      has(BookingErrorCode.OutsideWorkingHours) ||
      has(BookingErrorCode.PractitionerTimeOff) ||
      has(BookingErrorCode.RoomOutsideWorkingHours) ||
      has(BookingErrorCode.RoomBlockout) ||
      has(BookingErrorCode.DailyCapExceeded) ||
      has(BookingErrorCode.WeeklyCapExceeded),
    practitioner:
      has(BookingErrorCode.PractitionerOverlap) ||
      has(BookingErrorCode.PractitionerNotCapable) ||
      has(BookingErrorCode.OutsideWorkingHours) ||
      has(BookingErrorCode.PractitionerTimeOff) ||
      has(BookingErrorCode.PractitionerNotActiveMember) ||
      has(BookingErrorCode.DailyCapExceeded) ||
      has(BookingErrorCode.WeeklyCapExceeded),
    room:
      has(BookingErrorCode.RoomOverlap) ||
      has(BookingErrorCode.RoomDisabled) ||
      has(BookingErrorCode.RoomNotCapable) ||
      has(BookingErrorCode.RoomOutsideWorkingHours) ||
      has(BookingErrorCode.RoomBlockout),
  };
}

/** Mirrors the backend: only a live, undelivered booking moves or terminates. */
export const canReschedule = (b: Pick<BookingView, 'status' | 'delivered'>): boolean =>
  b.status === 'booked' && !b.delivered;
export const canTerminate = canReschedule;

export const isPast = (b: Pick<BookingView, 'endsAt'>, now: Date): boolean =>
  Date.parse(b.endsAt) < now.getTime();

/** Plan-apply staleness: sessions of the submitted set absent from a fresh plan. */
export function staleSessions<T extends { startsAt: string; practitionerUid: string }>(
  submitted: readonly T[],
  fresh: readonly { startsAt: string; practitionerUid: string }[],
): T[] {
  const key = (s: { startsAt: string; practitionerUid: string }) =>
    `${Date.parse(s.startsAt)}|${s.practitionerUid}`;
  const alive = new Set(fresh.map(key));
  return submitted.filter((s) => !alive.has(key(s)));
}

/**
 * ZOY-338: an unchanged reschedule submission must be named, not silently
 * "saved". Compares the draft against the booking's own facts; travel left
 * empty counts as unchanged (the request omits it).
 */
export function rescheduleUnchanged(
  b: Pick<
    BookingView,
    | 'startsAt'
    | 'endsAt'
    | 'practitionerUid'
    | 'locationType'
    | 'roomId'
    | 'visitAddress'
    | 'travelAllowanceMinutes'
    | 'joinKind'
    | 'joinUrl'
  >,
  draft: {
    startsAt: Date | null;
    endsAt: Date | null;
    practitionerUid: string;
    mode: BookingView['locationType'];
    roomId: string;
    address: string;
    travel: string;
    joinKind?: JoinKind | '';
    joinUrl?: string;
  },
): boolean {
  if (!draft.startsAt || !draft.endsAt) return false;
  return (
    draft.startsAt.toISOString() === b.startsAt &&
    draft.endsAt.toISOString() === b.endsAt &&
    draft.practitionerUid === b.practitionerUid &&
    draft.mode === b.locationType &&
    (draft.mode !== 'in_clinic' || (draft.roomId || null) === b.roomId) &&
    // ZOY-684: on an online booking the destination is part of the same booking.
    (draft.mode !== 'online' ||
      ((draft.joinKind || null) === (b.joinKind ?? null) &&
        (draft.joinKind !== 'external_url' ||
          (draft.joinUrl ?? '').trim() === (b.joinUrl ?? '')))) &&
    (draft.mode !== 'off_site' ||
      (draft.address.trim() === (b.visitAddress ?? '') &&
        (draft.travel.trim() === '' || Number(draft.travel) === b.travelAllowanceMinutes)))
  );
}

/**
 * ZOY-342: the By-room lens must not lose bookings. Whatever does not land
 * in a rendered room column — no room at all, or a room that is disabled /
 * unknown — belongs to the honest "no room" group, so every booking of the
 * day appears exactly once across the columns.
 */
export function unassignedRoomItems<T extends { roomId: string | null }>(
  bookings: readonly T[],
  renderedRoomIds: readonly string[],
): T[] {
  const ids = new Set(renderedRoomIds);
  return bookings.filter((b) => b.roomId === null || !ids.has(b.roomId));
}

/** ZOY-408: the row's own display identity — never a per-booking fetch. */
export function bookingPatientName(b: Pick<BookingView, 'patient'>, fallback = '—'): string {
  return b.patient ? `${b.patient.firstName} ${b.patient.lastName}` : fallback;
}

/** Resolve a patient-filter chip label from the rows already on screen. */
export function patientNameFromRows(
  rows: readonly Pick<BookingView, 'patientId' | 'patient'>[],
  patientId: string,
  fallback = '—',
): string {
  const hit = rows.find((r) => r.patientId === patientId);
  return hit ? bookingPatientName(hit, fallback) : fallback;
}
